/* eslint-disable indent */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import Popover from '@mui/material/Popover'
import Autocomplete from '@mui/lab/Autocomplete'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import clsx from 'clsx'
import { withStyles } from '~/themes/useStyles'

import Icon from '~/components/common/Icon'
import TextInput from '~/components/common/TextInput'
import style from './style'
import OverLay from '~/components/common/OverLay'
import { COLORS } from '~/themes/common'
import Checkbox from '../Checkbox'
import Button from '../Button'

const SelectionSearch = ({
  classes: classesList,
  label,
  placeholder,
  searchPlaceholder,
  value,
  disabled,
  className,
  options,
  onSelected,
  onInputChange,
  onClear,
  containerClassName,
  wrapperClassName,
  actionOnFocus,
  actionOnBlur,
  autoAsync,
  initAsyncOptions,
  apiAction,
  handleFilterOption,
  overlay,
  endDotDotDot,
  multiple,
  ...props
}) => {
  const valueBoxRef = useRef()

  const [loading, setLoading] = useState(false)
  const [optionsFromAsyncCall, setOptionsFromAsyncCall] = useState([])
  const [textInputValue, setTextInputValue] = useState('')
  const [noOptionsTxt, setNoOptionsTxt] = useState('Type to search')

  const [anchorEl, setAnchorEl] = useState(null)

  const {
    addLabel,
    optionLabel,
    checkbox,
    optionItem,
    buttonContainer,
    ...classes
  } = classesList

  const handleFocus = (e) => {
    setAnchorEl(e.currentTarget)
    if (typeof actionOnFocus === 'function') actionOnFocus()
  }

  const handleBlur = () => {
    setAnchorEl(null)
    if (typeof actionOnFocus === 'function') actionOnBlur()
  }

  const handleBlurApply = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    if (initAsyncOptions) setOptionsFromAsyncCall(initAsyncOptions)
  }, [initAsyncOptions])

  const id = anchorEl ? 'selection-search-input-popper' : undefined

  const getOptionsFromRequest = async (keyword) => {
    if (!keyword) return
    setOptionsFromAsyncCall([])
    setNoOptionsTxt('Type to search')
    if (textInputValue === '') {
      return
    }
    setLoading(true)
    try {
      const res = await apiAction(keyword)
      setLoading(false)
      const optionsData = handleFilterOption(res)
      if (optionsData.length === 0) {
        setNoOptionsTxt('No options')
      }
      setOptionsFromAsyncCall(optionsData)
    } catch (e) {
      setNoOptionsTxt('Something went wrong')
      setLoading(false)
      setOptionsFromAsyncCall([])
    }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      getOptionsFromRequest(textInputValue)
    }, 500)

    return () => clearTimeout(timeout)
  }, [textInputValue])

  const onInputChangeLocal = (val) => {
    setTextInputValue(val)
  }

  const renderOption = (selectProps, item, { selected }) => (
    <li {...selectProps}>
      <div className={optionItem}>
        <Checkbox
          className={checkbox}
          checked={selected}
          onClick={(e) => e.preventDefault()}
        />
        <span className={clsx(optionLabel, { multiple })}>
          {item?.label || ''}
        </span>
      </div>
    </li>
  )

  const renderTags = () => <></>

  const getOption = useCallback(
    () => (autoAsync ? optionsFromAsyncCall : options),
    [autoAsync, optionsFromAsyncCall, options],
  )

  const getTextValue = () =>
    multiple
      ? getOption()
          .filter((x) => (value || []).includes(x.value))
          .map((x) => x.label || '')[0] || ''
      : getOption().find((opt) => opt.value === value)?.label || ''

  const getAddAnchor = () => {
    const list = getOption().filter((x) => (value || []).includes(x.value))
    return list.length > 1 ? (
      <span className={addLabel}>{`+${list.length - 1}`}</span>
    ) : (
      ''
    )
  }

  useEffect(() => {
    // Reset Input if there isn't any option match the current value
    if (
      !!value &&
      !multiple &&
      getOption().every((opt) => opt.value !== value) &&
      onClear
    ) {
      onClear()
    }
  }, [getOption, value])

  return (
    <div
      className={clsx(classes.fullWidth, wrapperClassName)}
      style={{ zIndex: anchorEl ? 50 : '' }}
    >
      <TextInput
        checkRemoveCursor
        aria-describedby={id}
        label={label}
        variant="outlined"
        size="small"
        placeholder={placeholder}
        inputRef={valueBoxRef}
        className={containerClassName}
        InputProps={{
          onFocus: handleFocus,
          endAdornment: (
            <>
              {multiple && getAddAnchor()}
              {onClear &&
              ((!multiple && !!value) ||
                (multiple && !!value && value.length > 0)) ? (
                <IconButton
                  size="small"
                  onClick={onClear}
                  style={{ padding: 10 }}
                >
                  <Icon
                    name="close"
                    color={COLORS.palette.primaryBlue}
                    size={10}
                  />
                </IconButton>
              ) : anchorEl ? (
                <IconButton
                  size="small"
                  disabled={disabled}
                  style={{ padding: 0 }}
                >
                  <KeyboardArrowUp color="primary" />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => valueBoxRef.current.focus()}
                >
                  <KeyboardArrowDown
                    color={disabled ? 'disabled' : 'primary'}
                  />
                </IconButton>
              )}
            </>
          ),
        }}
        value={getTextValue()}
        disabled={disabled}
        endDotDotDot={endDotDotDot}
      />
      <Popover
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        placement="bottom-start"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        className={classes.popover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Autocomplete
          open
          multiple={multiple}
          {...props}
          {...(multiple
            ? {
                renderOption,
                renderTags,
                disableCloseOnSelect: true,
                onSelected: () => {},
                value: (getOption() || []).filter((x) =>
                  value.includes(x.value),
                ),
                open: true,
              }
            : {})}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          className={clsx(classes.root, className)}
          classes={{
            ...classes,
            paper: classes.paper,
            option: clsx(classes.option, { multiple }),
            popperDisablePortal: classes.popperDisablePortal,
          }}
          loading={loading}
          options={getOption()}
          noOptionsText={
            <Typography variant="subtitle2">{noOptionsTxt}</Typography>
          }
          disableClearable
          openOnFocus
          disablePortal
          onChange={(e, sltObj) => {
            if (!multiple) {
              onSelected(sltObj.value)
              handleBlur()
            } else {
              onSelected(sltObj.map((x) => x.value))
            }
          }}
          onInputChange={(e) =>
            autoAsync
              ? onInputChangeLocal(e.target.value)
              : onInputChange(e.target.value)
          }
          getOptionLabel={(option) => option.label || ''}
          popupIcon={
            <KeyboardArrowDown color={disabled ? 'disabled' : 'primary'} />
          }
          renderInput={(params) => (
            <TextInput
              {...params}
              autoFocus
              InputProps={{
                ...params.InputProps,
                endAdornment: false,
                style: {
                  height: 32,
                  padding: 0,
                },
                onBlur: () => {
                  handleBlur()
                },
              }}
              placeholder={searchPlaceholder}
            />
          )}
        />
        {!!multiple && (
          <div className={buttonContainer}>
            <Button
              onClick={() => {
                onSelected(null, 'apply')
                handleBlurApply()
              }}
              onMouseDown={(e) => {
                e.preventDefault()
              }}
            >
              Apply
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                onSelected(null, 'cancel')
                handleBlur()
              }}
              onMouseDown={(e) => {
                e.preventDefault()
              }}
            >
              Cancel
            </Button>
          </div>
        )}
      </Popover>
      {overlay && Boolean(anchorEl) && <OverLay />}
    </div>
  )
}

SelectionSearch.defaultProps = {
  value: '',
  className: '',
  label: '',
  placeholder: '',
  disabled: false,
  onSelected: () => {},
  onInputChange: () => {},
  searchPlaceholder: '',
  options: [],
  onClear: null,

  actionOnFocus: () => {},
  actionOnBlur: () => {},
  autoAsync: false,
  initAsyncOptions: [],
  apiAction: () => {},
  handleFilterOption: (e) => e,
  overlay: true,
  endDotDotDot: false,
  multiple: false,
  containerClassName: '',
  wrapperClassName: '',
}

SelectionSearch.propTypes = {
  value: PropTypes.string,
  className: PropTypes.string,
  classes: PropTypes.shape().isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  disabled: PropTypes.bool,
  onSelected: PropTypes.func,
  onInputChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape()),
  onClear: PropTypes.func,
  containerClassName: PropTypes.string,
  wrapperClassName: PropTypes.string,
  actionOnFocus: PropTypes.func,
  actionOnBlur: PropTypes.func,
  autoAsync: PropTypes.bool,
  initAsyncOptions: PropTypes.arrayOf(PropTypes.shape()),
  apiAction: PropTypes.func,
  handleFilterOption: PropTypes.func,
  overlay: PropTypes.bool,
  endDotDotDot: PropTypes.bool,
  multiple: PropTypes.bool,
}

export default withStyles(style)(SelectionSearch)
